import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslations } from '@altermaind/core/i18n';
import { useThemeValue } from '@altermaind/web/ui';
import { convertSizeToNumber } from '@/_shared/helpers/convertSizeToNumber';
import { useNotification } from '@/_shared/hooks/useNotification';
import { setColor } from '../actions';
import {
  CallResponseGetAspectForApp,
  getAspectCall,
  getAspectForAppCall,
  putAspectSaveCall,
} from '../api';
import {
  CallResponsePostAspectGenerate,
  postAspectGenerateCall,
} from '../api/postAspectGenerateCall';
import { getColor } from '../selectors';

export const useAspect = () => {
  const t = useTranslations('settings.tabs.aspect.errors');
  const tShared = useTranslations();
  const dispatch = useDispatch();
  const { notify } = useNotification();

  const buttonGenerateLogoRef = useRef<HTMLButtonElement>(null);
  const inputSiteRef = useRef<HTMLInputElement>(null);
  const [submittedSite, setSubmittedSite] = useState<string | undefined>(undefined);
  const [logoLight, setLogoLight] = useState<string | undefined>(undefined);
  const [logoDark, setLogoDark] = useState<string | undefined>(undefined);
  const [dark, setDark] = useState(true);
  const gapXS = convertSizeToNumber(`${useThemeValue('sizes', 'xs')}`);
  const gapXXL = convertSizeToNumber(`${useThemeValue('sizes', 'xxl')}`);
  const gapXXXS = convertSizeToNumber(`${useThemeValue('space', 'xxxs')}`);

  const {
    execute: executePostAspectGenerate,
    loading: loadingPostAspectGenerate,
    error: errorPostAspectGenerate,
    success: successPostAspectGenerate,
    response: responsePostAspectGenerate,
  } = postAspectGenerateCall.hook();
  const { execute: executeGetAspect } = getAspectCall.hook();
  const { execute: executeGetAspectForApp } = getAspectForAppCall.hook();
  const { execute: executePutAspectSave, loading: loadingPutAspectSave } = putAspectSaveCall.hook();

  const {
    control,
    watch,
    formState: { isValid },
    handleSubmit,
    trigger,
    setValue,
  } = useForm();

  const fieldValues = watch();

  const refreshDesign = useCallback(() => {
    executeGetAspectForApp({
      successSaga: ({ response }) => {
        const responseAspect = response as CallResponseGetAspectForApp;
        if (responseAspect?.color?.dark) {
          window.accentDark = responseAspect.color.dark;
        }
        if (responseAspect?.color?.light) {
          window.accentLight = responseAspect.color.light;
        }
        if (responseAspect?.color?.dark || responseAspect?.color?.light) {
          window.dispatchEvent(new CustomEvent('accent-change'));
        }
      },
    });
  }, [executeGetAspectForApp]);

  const colorSuggested = useSelector(getColor);

  const onSelectedNewColor = useCallback(
    (colorId: string) => {
      if (colorSuggested !== colorId) {
        dispatch(setColor(colorId));
      }
    },
    [colorSuggested, dispatch],
  );

  const [dynamicHeight, setDynamicHeight] = useState<string | undefined>(undefined);

  const updateHeight = useCallback(
    (height: number) => {
      const newHeight = `calc(${height}px - ${(gapXXXS || 0) * 2}px)`;
      setDynamicHeight(newHeight);
    },
    [gapXXXS],
  );

  const generateLogo = useCallback(
    (remaining: number) => {
      if (remaining === 0) {
        return;
      }
      setLogoDark(undefined);
      setLogoLight(undefined);
      setSubmittedSite(fieldValues.site);
      executePostAspectGenerate({
        link: fieldValues.site,
        successSaga: ({ response }) => {
          const internalResponsePostAspectGenerate = response as CallResponsePostAspectGenerate;
          if (internalResponsePostAspectGenerate?.logo?.light) {
            setLogoLight(internalResponsePostAspectGenerate?.logo?.light);
            setValue('uploadLogoLight', { value: internalResponsePostAspectGenerate?.logo?.light });
          }
          if (internalResponsePostAspectGenerate?.logo?.dark) {
            setLogoDark(internalResponsePostAspectGenerate?.logo?.dark);
            setValue('uploadLogoDark', { value: internalResponsePostAspectGenerate?.logo?.dark });
          }
          if (internalResponsePostAspectGenerate?.color?.id) {
            dispatch(setColor(internalResponsePostAspectGenerate?.color?.id));
          }
          notify(tShared('success.genericApiSuccess'), 'success');
          executeGetAspect({});
        },
        errorSaga: ({ error }) => {
          const LIMIT_EXCEEDED_STATUS = 429;
          if (error.status === LIMIT_EXCEEDED_STATUS) {
            notify(t('designQuotaExceeded'), 'error');
          } else {
            notify(tShared('errors.genericApiError'), 'error');
          }
        },
      });
    },
    [
      dispatch,
      executePostAspectGenerate,
      fieldValues.site,
      notify,
      executeGetAspect,
      setValue,
      t,
      tShared,
    ],
  );

  const onSubmit = useCallback(() => {
    executePutAspectSave({
      logo: {
        light: fieldValues?.uploadLogoLight?.value || '',
        dark: fieldValues?.uploadLogoDark?.value || '',
      },
      colorId: colorSuggested || undefined,
      successSaga: () => {
        notify(tShared('success.genericApiSuccess'), 'success');
        refreshDesign();
      },
      errorSaga: () => {
        notify(tShared('errors.genericApiError'), 'error');
      },
    });
  }, [fieldValues, executePutAspectSave, colorSuggested, notify, tShared, refreshDesign]);

  const isEmptyObject = (obj: Record<string, unknown>) => {
    return Object.keys(obj).length === 0;
  };
  const isSiteValueChanged = useMemo(() => {
    return submittedSite !== fieldValues.site;
  }, [submittedSite, fieldValues.site]);

  const errorPostAspectGenerateLogo = useMemo(() => {
    return (
      !isSiteValueChanged &&
      successPostAspectGenerate &&
      (!responsePostAspectGenerate?.logo || isEmptyObject(responsePostAspectGenerate?.logo))
    );
  }, [successPostAspectGenerate, responsePostAspectGenerate, isSiteValueChanged]);

  const errorPostAspectGenerateColors = useMemo(() => {
    return (
      successPostAspectGenerate &&
      (!responsePostAspectGenerate?.color || isEmptyObject(responsePostAspectGenerate?.color))
    );
  }, [successPostAspectGenerate, responsePostAspectGenerate]);

  useEffect(() => {
    const inputSiteElement = inputSiteRef.current;
    if (inputSiteElement) {
      updateHeight(inputSiteElement.clientHeight);
    }

    const handleResize = () => {
      const internalRef = inputSiteRef.current;
      if (internalRef) {
        updateHeight(internalRef.clientHeight);
      }
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('load', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('load', handleResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputSiteRef.current, updateHeight]);

  useEffect(() => {
    if (logoLight) {
      setValue('uploadLogoLight', { value: logoLight });
    }
    if (logoDark) {
      setValue('uploadLogoDark', { value: logoDark });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logoLight, logoDark]);

  return {
    buttonGenerateLogoRef,
    inputSiteRef,
    logoLight,
    setLogoLight,
    logoDark,
    setLogoDark,
    gapXS,
    gapXXL,
    gapXXXS,
    colorSuggested,
    onSelectedNewColor,
    generateLogo,
    control,
    isValid,
    loadingPostAspectGenerate,
    errorPostAspectGenerate,
    errorPostAspectGenerateLogo,
    errorPostAspectGenerateColors,
    isSiteValueChanged,
    dark,
    setDark,
    handleSubmit,
    onSubmit,
    loadingPutAspectSave,
    fieldValues,
    dynamicHeight,
    refreshDesign,
    trigger,
  };
};
